import type { ResourceOutput } from '@cotera/api';
import { DG } from '../dg';
import { Lazy } from './lazy';
import { Folder } from './folder';
import { fromIsoString, toIsoString } from './iso-date';
import {
  parseDocumentState,
  titleFromDocumentState,
} from './parse-document-state';
import { WorkspacePromptEditor } from './workspace-prompt-editor';
import type { WorkspaceDomainLoader } from './workspace-prefetch';

export type DocumentResourceOutput = Extract<
  ResourceOutput,
  { type: 'document' }
>;

export type DocumentData = {
  readonly id: string;
  readonly orgId: string;
  readonly ownerId: string;
  readonly editorState: DG.DocumentEditor.DocumentState;
  readonly description: string | null;
  readonly status: 'active' | 'archived';
  readonly createdAt: Date;
  readonly updatedAt: Date;
  /** Present when constructed from resource/meta that includes placement. */
  readonly folderId?: string | null;
};

export type SerializedDocument = {
  readonly id: string;
  readonly orgId: string;
  readonly ownerId: string;
  readonly editorState: DG.DocumentEditor.DocumentState;
  readonly description: string | null;
  readonly status: 'active' | 'archived';
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly folderId?: string | null;
};

export type DocumentHydration = {
  readonly loadFolder: () => Promise<Folder | null>;
  readonly preloadedFolder?: Folder | null;
  /** Resolves @mentions when compiling this document’s workspace prompt. */
  readonly workspaceDomainLoader: WorkspaceDomainLoader;
};

function toDocumentData(serialized: SerializedDocument): DocumentData {
  return {
    id: serialized.id,
    orgId: serialized.orgId,
    ownerId: serialized.ownerId,
    editorState: parseDocumentState(serialized.editorState),
    description: serialized.description,
    status: serialized.status,
    createdAt: fromIsoString(serialized.createdAt),
    updatedAt: fromIsoString(serialized.updatedAt),
    folderId: serialized.folderId,
  };
}

/**
 * Standalone document resource (TipTap JSON in {@link DocumentData#editorState}),
 * with {@link WorkspacePromptEditor} for prompt compilation and optional lazy folder placement.
 */
export class Document {
  private readonly _data: DocumentData;
  private readonly _workspacePromptEditor: WorkspacePromptEditor;
  readonly folder: Lazy<Folder | null>;

  private constructor(data: DocumentData, hydration: DocumentHydration) {
    this._data = data;
    this._workspacePromptEditor = new WorkspacePromptEditor(
      data.editorState,
      hydration.workspaceDomainLoader
    );
    this.folder = new Lazy(hydration.loadFolder, hydration.preloadedFolder);
  }

  get id(): string {
    return this._data.id;
  }

  get orgId(): string {
    return this._data.orgId;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get title(): string | null {
    return titleFromDocumentState(this._data.editorState);
  }

  get description(): string | null {
    return this._data.description;
  }

  get status(): 'active' | 'archived' {
    return this._data.status;
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  get folderId(): string | null | undefined {
    return this._data.folderId;
  }

  /**
   * Workspace-oriented prompt editor (no data graph); same TipTap document as persisted JSON.
   */
  get editorState(): WorkspacePromptEditor {
    return this._workspacePromptEditor;
  }

  /** Same instance as {@link Document#editorState}. */
  getEditorState(): WorkspacePromptEditor {
    return this._workspacePromptEditor;
  }

  /** First H1 from editor state, or null. */
  displayTitle(): string | null {
    return this.title;
  }

  get documentData(): DocumentData {
    return { ...this._data };
  }

  serialize(): SerializedDocument {
    const base: SerializedDocument = {
      id: this._data.id,
      orgId: this._data.orgId,
      ownerId: this._data.ownerId,
      editorState: this._data.editorState,
      description: this._data.description,
      status: this._data.status,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
    if (this._data.folderId !== undefined) {
      return { ...base, folderId: this._data.folderId };
    }
    return base;
  }

  static fromSerialized(
    serialized: SerializedDocument,
    hydration: DocumentHydration
  ): Document {
    return new Document(toDocumentData(serialized), hydration);
  }

  static fromResourceOutput(
    output: DocumentResourceOutput,
    workspaceDomainLoader: WorkspaceDomainLoader
  ): Document {
    const d = output.document;
    const folderId = output.meta?.folderId ?? null;
    const serialized: SerializedDocument = {
      id: d.id,
      orgId: d.orgId,
      ownerId: d.ownerId,
      editorState: parseDocumentState(d.editorState),
      description: d.description,
      status: d.status,
      createdAt: toIsoString(d.createdAt),
      updatedAt: toIsoString(d.updatedAt),
      ...(folderId !== null ? { folderId } : {}),
    };
    return Document.fromSerialized(serialized, {
      loadFolder: async () =>
        folderId !== null ? workspaceDomainLoader.loadFolder(folderId) : null,
      workspaceDomainLoader,
    });
  }
}
